import React, { useEffect, useRef, useState } from 'react';

import { ProvidersService } from '../services/ProvidersService';
import "./Search.css";
import "../pages/home.css";
import SearchResult from "./SearchResult";
import Map from "./Map";
import { Box, Pagination, Button } from "@mui/material";
import { Constants } from "../Constants";
import UseWindowDimensions from "./GetWindowDimensions";
import { useFillToFooterHeight } from "../hooks/useFillToFooterHeight";

function Search(props) {
  const [APIData, setAPIData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [numberOfColumns, setNumberOfColumns] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [viewMode, setViewMode] = useState(props.view || 'list');
  const windowDimensions = UseWindowDimensions();
  const outerContainerRef = useRef(null);

  useEffect(() => {
    ProvidersService.searchProviders(props.latitude, props.longitude, String(Number.parseFloat(props.accuracy) + Number.parseFloat(props.distance)), props.category, "", props.page, pageSize, props.city, props.state, props.bounds).then((response) => setAPIData(response));
  }, [props.category, props.distance, props.latitude, props.longitude, props.accuracy, props.page, pageSize, props.city, props.state, props.bounds?.minLatitude, props.bounds?.maxLatitude, props.bounds?.minLongitude, props.bounds?.maxLongitude]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const hasCityState = props.city && props.state;
    const hasBounds = props.bounds && props.bounds.minLatitude != null && props.bounds.maxLatitude != null && props.bounds.minLongitude != null && props.bounds.maxLongitude != null;
    if (hasBounds || hasCityState || (props.latitude && props.longitude && props.accuracy && props.distance)) {
      ProvidersService.searchProviders(props.latitude, props.longitude, String(Number.parseFloat(props.accuracy) + Number.parseFloat(props.distance)), props.category, "", 1, 100, props.city, props.state, props.bounds).then((response) => setMapData(response));
    }
  }, [props.category, props.distance, props.latitude, props.longitude, props.accuracy, props.city, props.state, props.bounds?.minLatitude, props.bounds?.maxLatitude, props.bounds?.minLongitude, props.bounds?.maxLongitude]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (APIData) {
      setTotalPages(Math.ceil((APIData.totalCount || 0) / pageSize));
    }
  }, [APIData, pageSize]);

  useEffect(() => {
    let columns = numberOfColumns;
    if (columns < 2) columns = 2;
    setPageSize(columns * 3);
  }, [numberOfColumns]);

  const handlePageChange = (event, value) => {
    props.setPage(value);
  };

  const toggleViewMode = () => {
    const next = viewMode === 'list' ? 'map' : 'list';
    setViewMode(next);
    props.setView(next);
  };

  // Haversine helper for bounds zoom calculation
  const haversineMiles = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const isDesktopView = typeof windowDimensions?.width === 'number' ? windowDimensions.width > 700 : true;
  const isCityStateSearch = Boolean(props.city && props.state);
  const hasBounds = props.bounds && props.bounds.minLatitude != null && props.bounds.maxLatitude != null && props.bounds.minLongitude != null && props.bounds.maxLongitude != null;
  const isGeoMode = hasBounds || (!isCityStateSearch && Boolean(props.latitude && props.longitude));

  const mapPlaces = mapData?.providers?.map((provider) => ({
    longitude: provider.longitude,
    latitude: provider.latitude,
    name: provider?.name,
    rating: provider?.rating,
    city: provider?.city,
    state: provider?.state,
    servicesProvided: provider?.servicesProvided,
    shortDescription: provider?.shortDescription,
    friendlyUrl: provider?.friendlyUrl,
    id: provider?.id,
    isMemberProvider: provider?.isMemberProvider
  })) || [];

  const cityMapPlaces = mapPlaces.filter((place) => {
    const latitude = Number.parseFloat(place.latitude);
    const longitude = Number.parseFloat(place.longitude);
    return (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180 &&
      (latitude !== 0 || longitude !== 0)
    );
  });

  const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[middle - 1] + sorted[middle]) / 2
      : sorted[middle];
  };

  const cityMapCenter = cityMapPlaces.length > 0
    ? {
      latitude: median(cityMapPlaces.map((place) => Number.parseFloat(place.latitude))),
      longitude: median(cityMapPlaces.map((place) => Number.parseFloat(place.longitude)))
    }
    : null;

  const hasGeoMapCenter = props?.accuracy && props?.latitude && props?.longitude && props?.distance;
  const boundsCenter = hasBounds ? {
    latitude: (props.bounds.minLatitude + props.bounds.maxLatitude) / 2,
    longitude: (props.bounds.minLongitude + props.bounds.maxLongitude) / 2
  } : null;
  const boundsHalfDiagonal = hasBounds ? haversineMiles(
    boundsCenter.latitude, boundsCenter.longitude,
    props.bounds.maxLatitude, props.bounds.maxLongitude
  ) : 0;
  const mapCenter = hasBounds && boundsCenter
    ? boundsCenter
    : isCityStateSearch && cityMapCenter
      ? cityMapCenter
      : (parseFloat(props.distance) >= Constants.EARTH_CIRCUMFERENCE / 2)
        ? { latitude: 40.009401, longitude: -98.053903 }
        : { latitude: props.latitude, longitude: props.longitude };
  const mapCircle = hasBounds
    ? undefined
    : isCityStateSearch
      ? undefined
      : (parseFloat(props.distance) < Constants.EARTH_CIRCUMFERENCE / 2)
        ? parseFloat(props.distance) + parseFloat(props.accuracy)
        : undefined;
  const mapZoom = hasBounds
    ? Math.log2(Constants.EARTH_CIRCUMFERENCE / (2 * boundsHalfDiagonal))
    : isCityStateSearch
      ? 12
      : (parseFloat(props.distance) >= Constants.EARTH_CIRCUMFERENCE / 2)
        ? 3
        : (Math.log2(Constants.EARTH_CIRCUMFERENCE / (2 * (parseFloat(props.distance) + parseFloat(props.accuracy)))));
  const shouldShowMap = hasBounds || hasGeoMapCenter || (isCityStateSearch && cityMapCenter);
  const displayedMapPlaces = isCityStateSearch ? cityMapPlaces : mapPlaces;

  const noResults = !APIData || !APIData?.providers || APIData?.providers?.length === 0;

  // Mobile only: size the results to the space above the footer (CSS calc() is the fallback)
  useFillToFooterHeight(outerContainerRef, !isDesktopView);

  return (
    <Box ref={outerContainerRef} className={`search-outer-container ${isDesktopView ? 'desktop' : 'mobile'}`}>
      <Box
        className={`search-list-pane ${!isDesktopView && viewMode !== 'list' ? 'hidden' : ''}`}
      >
        <Box style={{ display: "flex", flexWrap: "wrap" }}>
          {noResults ? (
            <h4 data-testid="no-results-msg">No items were found to match your search, please make another search.</h4>
          ) : (
            APIData?.providers?.map((item, key) => (
              <SearchResult key={key} k={key} isLast={APIData.providers.length - 1 === key} item={item} setColumnsOnParent={setNumberOfColumns} />
            ))
          )}
        </Box>

        <Box data-testid="next-page" display="flex" justifyContent="center" visibility={totalPages <= 1 ? "collapse" : "visible"}>
          <Pagination
            count={totalPages}
            page={props.page}
            onChange={handlePageChange}
            hidePrevButton={props.page === 1}
            hideNextButton={props.page === totalPages}
            color="primary"
            shape="rounded"
          />
        </Box>
      </Box>

      <Box
        className={`search-map-pane ${!isDesktopView && viewMode !== 'map' ? 'hidden' : ''}`}
      >
        {shouldShowMap && (
          <Map
            center={mapCenter}
            circle={mapCircle}
            zoom={mapZoom}
            places={displayedMapPlaces}
            visible={isDesktopView || viewMode === 'map'}
            searchCenter={hasBounds && boundsCenter ? boundsCenter : (props.latitude && props.longitude ? { latitude: Number(props.latitude), longitude: Number(props.longitude) } : null)}
            searchDistance={hasBounds ? boundsHalfDiagonal : Number.parseFloat(props.distance)}
            isGeoMode={isGeoMode}
            bounds={props.bounds || null}
          />
        )}
      </Box>

      {!isDesktopView && (
        <Button
          onClick={toggleViewMode}
          className="view-toggle-button"
          variant="contained"
          color="primary"
        >
          {viewMode === 'list' ? 'Map' : 'List'}
        </Button>
      )}
    </Box>
  );
}

export default Search;
